/**
 * Operation execution status — colors for BMR Tracking / DAG / tables.
 * Palette chosen so adjacent legend swatches are easy to tell apart.
 *
 * Critical supervisor approval: "Completed - Under Review" blocks downstream deps.
 * Non-critical: "Completed - Review Pending" — deps may proceed; supervisor reviews later.
 */

export const EXECUTION_STATUS_STYLES = {
  Pending: {
    label: 'Pending',
    tagColor: 'error',
    color: '#dc2626',
    bg: '#fef2f2',
    fillBg: '#fecaca',
    border: '#dc2626',
    text: '#991b1b',
    filter:
      'brightness(0) saturate(100%) invert(32%) sepia(93%) saturate(1875%) hue-rotate(340deg) brightness(96%) contrast(93%)',
  },
  Running: {
    label: 'Running',
    tagColor: 'processing',
    color: '#2563eb',
    bg: '#eff6ff',
    fillBg: '#bfdbfe',
    border: '#2563eb',
    text: '#1e3a8a',
    filter:
      'brightness(0) saturate(100%) invert(31%) sepia(98%) saturate(1752%) hue-rotate(211deg) brightness(97%) contrast(96%)',
  },
  Completed: {
    label: 'Completed',
    tagColor: 'success',
    color: '#16a34a',
    bg: '#f0fdf4',
    fillBg: '#bbf7d0',
    border: '#16a34a',
    text: '#14532d',
    filter:
      'brightness(0) saturate(100%) invert(48%) sepia(100%) saturate(362%) hue-rotate(82deg) brightness(91%) contrast(90%)',
  },
  CompletedUnderReview: {
    label: 'Completed - Under Review',
    tagColor: 'purple',
    color: '#7c3aed',
    bg: '#f5f3ff',
    fillBg: '#ddd6fe',
    border: '#7c3aed',
    text: '#4c1d95',
    filter:
      'brightness(0) saturate(100%) invert(28%) sepia(89%) saturate(3500%) hue-rotate(252deg) brightness(95%) contrast(95%)',
  },
  CompletedReviewPending: {
    label: 'Completed - Review Pending',
    tagColor: 'cyan',
    color: '#0891b2',
    bg: '#ecfeff',
    fillBg: '#a5f3fc',
    border: '#0891b2',
    text: '#155e75',
    filter:
      'brightness(0) saturate(100%) invert(52%) sepia(85%) saturate(1200%) hue-rotate(155deg) brightness(92%) contrast(92%)',
  },
  CompletedReviewed: {
    label: 'Completed - Reviewed',
    tagColor: 'green',
    color: '#0f766e',
    bg: '#f0fdfa',
    fillBg: '#99f6e4',
    border: '#0f766e',
    text: '#134e4a',
    filter:
      'brightness(0) saturate(100%) invert(38%) sepia(45%) saturate(1200%) hue-rotate(130deg) brightness(90%) contrast(92%)',
  },
  Paused: {
    label: 'Paused',
    tagColor: 'gold',
    color: '#ca8a04',
    bg: '#fefce8',
    fillBg: '#fef08a',
    border: '#ca8a04',
    text: '#713f12',
    filter:
      'brightness(0) saturate(100%) invert(72%) sepia(85%) saturate(1100%) hue-rotate(1deg) brightness(95%) contrast(95%)',
  },
  Hold: {
    label: 'Hold',
    tagColor: 'error',
    color: '#be123c',
    bg: '#fff1f2',
    fillBg: '#fecdd3',
    border: '#be123c',
    text: '#881337',
    filter:
      'brightness(0) saturate(100%) invert(22%) sepia(95%) saturate(4000%) hue-rotate(328deg) brightness(90%) contrast(95%)',
  },
  Skipped: {
    label: 'Skipped',
    tagColor: 'default',
    color: '#64748b',
    bg: '#f1f5f9',
    fillBg: '#cbd5e1',
    border: '#64748b',
    text: '#334155',
    filter:
      'brightness(0) saturate(100%) invert(45%) sepia(8%) saturate(900%) hue-rotate(176deg) brightness(92%) contrast(86%)',
  },
};

/** Legend rows for BMR Tracking / DAG (order matches typical workflow). */
export const EXECUTION_STATUS_LEGEND = [
  { key: 'Pending', description: 'Not started' },
  { key: 'Running', description: 'In progress' },
  { key: 'CompletedUnderReview', description: 'Done — awaiting supervisor (blocks next op if critical)' },
  { key: 'CompletedReviewPending', description: 'Done — supervisor review open (non-critical, deps may run)' },
  { key: 'Completed', description: 'Done — no supervisor gate' },
  { key: 'CompletedReviewed', description: 'Done — supervisor approved' },
  { key: 'Paused', description: 'Paused' },
  { key: 'Hold', description: 'Batch on hold' },
];

/**
 * @param {string|object} statusOrRow - raw status string or API row
 * @returns {{ key: string, label: string, sublabel?: string, raw: string, style: object }}
 */
export function resolveOperationExecutionStatus(statusOrRow) {
  const row =
    typeof statusOrRow === 'object' && statusOrRow !== null ? statusOrRow : { status: statusOrRow };
  const raw = String(row.status || row.operation_status || '').trim();
  const lower = raw.toLowerCase();

  let key = 'Pending';
  let sublabel;

  if (
    lower === 'pending approval' ||
    lower.includes('under review') ||
    row.downstream_blocked === true ||
    row.downstream_blocked === 'true'
  ) {
    key = 'CompletedUnderReview';
  } else if (lower.includes('review pending')) {
    key = 'CompletedReviewPending';
  } else if (lower.includes('reviewed')) {
    key = 'CompletedReviewed';
  } else if (
    row.supervisor_review_pending === true ||
    row.supervisor_review_pending === 'true' ||
    (row.is_supervisor_approval_required &&
      String(row.approval_status || '').toUpperCase() === 'PENDING' &&
      String(row.batch_operation_status || '').toLowerCase() === 'completed')
  ) {
    key = 'CompletedReviewPending';
  } else if (lower.includes('hold')) {
    key = 'Hold';
  } else if (lower.includes('pause')) {
    key = 'Paused';
  } else if (lower.includes('skip')) {
    key = 'Skipped';
  } else if (lower.includes('run') || lower.includes('progress')) {
    key = 'Running';
  } else if (lower.includes('complet') || lower.includes('done')) {
    key = 'Completed';
  } else if (lower.includes('pend')) {
    key = 'Pending';
  }

  const style = EXECUTION_STATUS_STYLES[key] || EXECUTION_STATUS_STYLES.Pending;

  if (!sublabel && row.reviewed_by) {
    const who = String(row.reviewed_by).trim();
    const at = row.reviewed_at ? String(row.reviewed_at).trim() : '';
    sublabel = at ? `${who} · ${at}` : who;
  } else if (!sublabel && key === 'CompletedReviewPending') {
    sublabel = 'Awaiting supervisor review';
  }

  return {
    key,
    label: style.label,
    sublabel,
    raw: raw || style.label,
    style,
  };
}

export function getExecutionStatusConfig(key) {
  return EXECUTION_STATUS_STYLES[key] || EXECUTION_STATUS_STYLES.Pending;
}

/** Ant Design Tag color for batch/operation status columns. */
export function getExecutionStatusTagColor(statusOrRow) {
  return resolveOperationExecutionStatus(statusOrRow).style.tagColor;
}

/** Map API / Batch_Config status to DAG node status key (ArchiMateFlow uses style keys). */
export function toDagNodeStatus(statusOrRow) {
  const { key, label } = resolveOperationExecutionStatus(statusOrRow);
  if (key === 'Running') return 'In Progress';
  return label;
}
